import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNetworkStatus } from '@/shared/hooks'
import { offlineQueue } from '@/lib/offline-queue'
import { apiClient } from '@/lib/api-client'
import { useOfflineTickStore } from './useOfflineTickStore'
import { GROCERY_LIST_QUERY_KEY } from './useGroceryList'
import { logger } from '@/lib/logger'

const MAX_FAIL_COUNT = 5

// Module-level lock survives component re-mounts, preventing concurrent flushes
let isSyncing = false

export function useOfflineSync() {
  const { isOnline } = useNetworkStatus()
  const queryClient = useQueryClient()
  const { removePending } = useOfflineTickStore()

  useEffect(() => {
    if (!isOnline || isSyncing) return

    async function flush() {
      isSyncing = true
      try {
        const pending = await offlineQueue.dequeue()
        let synced = 0
        for (const mutation of pending) {
          if ((mutation.failCount ?? 0) >= MAX_FAIL_COUNT) {
            // Permanently failed — discard from queue and clear pending state
            if (mutation.id != null) await offlineQueue.remove(mutation.id)
            const itemId = mutation.meta?.itemId
            if (itemId) removePending(itemId)
            logger.warn('Offline mutation discarded after max retries', { path: mutation.path })
            continue
          }

          try {
            switch (mutation.method) {
              case 'PATCH':
                await apiClient.patch<void>(mutation.path, mutation.body)
                break
              case 'POST':
                await apiClient.post<void>(mutation.path, mutation.body)
                break
              case 'PUT':
                await apiClient.put<void>(mutation.path, mutation.body)
                break
              case 'DELETE':
                await apiClient.delete<void>(mutation.path)
                break
            }
            if (mutation.id != null) await offlineQueue.remove(mutation.id)
            const itemId = mutation.meta?.itemId
            if (itemId) removePending(itemId)
            synced++
          } catch (err) {
            // Increment failCount in place to preserve queue ordering
            if (mutation.id != null) {
              await offlineQueue.update(mutation.id, {
                failCount: (mutation.failCount ?? 0) + 1,
              })
            }
            logger.warn('Offline sync failed for mutation', { path: mutation.path, failCount: (mutation.failCount ?? 0) + 1 })
          }
        }
        if (synced > 0) {
          queryClient.invalidateQueries({ queryKey: GROCERY_LIST_QUERY_KEY })
        }
      } finally {
        isSyncing = false
      }
    }

    flush()
  }, [isOnline, queryClient, removePending])
}
